import { useState } from 'react'

interface EditFlowStaleHpoBannerProps {
  /** The previously-sent clinical summary, decoded from the baseline ref. */
  previousSummary: string
}

/**
 * Edit-order flow banner for GEN-2608.
 *
 * When a provider edits the clinical summary on a submitted order, the
 * previously-extracted HPO terms in the Indications card are NOT
 * automatically refreshed -- they still reflect the original summary that
 * was sent to the lab. Without an explicit cue, the order can silently go
 * out with phenotypes that no longer match the narrative.
 *
 * This banner provides the cue and gives the provider a way to read the
 * original summary text alongside their edits, without yet wiring up the
 * full re-extraction flow (tracked in GEN-2596).
 *
 * The previously-extracted phenotypes themselves are already visible in
 * the Indications card -- we don't duplicate them here. We only show the
 * previous narrative so the provider can mentally reconcile the two.
 *
 * Visual style mirrors `.info-banner` (the class used by AlertBanner) but
 * we render our own block-level container instead of routing through
 * AlertBanner because we need a multi-row layout (heading text, disclosure
 * toggle, expanded content) and AlertBanner forces children into a single
 * `<p>` element.
 */
export function EditFlowStaleHpoBanner({ previousSummary }: EditFlowStaleHpoBannerProps) {
  const [expanded, setExpanded] = useState(false)
  const hasPrevious = previousSummary.trim().length > 0

  return (
    <div
      className="flex flex-col gap-2 rounded-base border border-border-brand-subtle dark:border-brand-800 bg-background-brand-softer dark:bg-gray-800 px-3 pb-3 pt-4 sm:px-4 sm:pb-4 sm:pt-5"
      data-testid="edit-flow-stale-hpo-banner"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-100 dark:bg-gray-800 text-text-fg-info dark:text-brand-300">
          i
        </span>
        <p className="flex-1 text-xs leading-5 text-text-fg-info dark:text-brand-300 sm:text-sm">
          <span className="font-medium">Clinical summary edited.</span> The phenotypes in the
          Indications card were extracted from the previous version of the summary and may no
          longer reflect the current narrative. Please review them before sending.
        </p>
      </div>

      {hasPrevious && (
        <div className="ml-6">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-medium text-text-fg-info dark:text-brand-300 underline hover:text-brand-600 dark:hover:text-brand-400 sm:text-sm"
            aria-expanded={expanded}
            data-testid="edit-flow-stale-hpo-banner-toggle"
          >
            {expanded ? 'Hide previous summary' : 'View previous summary'}
          </button>
        </div>
      )}

      {expanded && hasPrevious && (
        <pre
          className="ml-6 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-3 font-sans text-xs leading-5 text-text-body dark:text-gray-400 shadow-xs"
          data-testid="edit-flow-stale-hpo-banner-previous"
        >
          {previousSummary}
        </pre>
      )}
    </div>
  )
}
